import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SessionStorageService } from './session-storage.service';
import { RightBridgeApiService } from './right-bridge-api.service';
import { ThemeService } from './theme.service';
import { DynamicResponse } from '../models/general-models';

@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private rbs = inject(RightBridgeApiService);
  private themeSvc = inject(ThemeService);

  private sessionId: string = this.ss.get('session');
  public baseUrl: string;
  private firm: string;
  private app: string;
  private org: string;
  private columnsPreferences;
  private sortPreferences;
  private orderPreferences;

  constructor() {
    this.baseUrl = this.rbs.baseUrl;
  }

  saveColumns(columns): Observable<void> {
    const url = `${this.baseUrl}/UserPreferences?prefName=columnVisibility`;
    let existing;
    let data;

    if (this.columnsPreferences) {
      const columnsKey = Object.keys(columns)[0];

      try {
        existing = JSON.parse(this.columnsPreferences);
      } catch (e) {
        existing = this.columnsPreferences;
      }

      existing[columnsKey] = columns[columnsKey];
      data = existing;
    } else {
      data = columns;
    }

    return this.rbs.postRequest<any>(url, data, null, null, 'rb');
  }

  saveSort(sort): Observable<void> {
    const url = `${this.baseUrl}/UserPreferences?prefName=sortOrder`;
    let existing;
    let data;

    if (this.sortPreferences) {
      const sortKey = Object.keys(sort)[0];

      try {
        existing = JSON.parse(this.sortPreferences);
      } catch (e) {
        existing = this.sortPreferences;
      }

      existing[sortKey] = sort[sortKey];
      data = existing;
    } else {
      data = sort;
    }

    return this.rbs.postRequest<any>(url, data, null, null, 'rb');
  }

  saveColOrder(colOrder): Observable<void> {
    const url = `${this.baseUrl}/UserPreferences?prefName=colOrder`;
    let existing;
    let data;

    if (this.orderPreferences) {
      const orderItem = Object.keys(colOrder)[0];

      try {
        existing = JSON.parse(this.orderPreferences);
      } catch (e) {
        existing = this.orderPreferences;
      }

      existing[orderItem] = colOrder[orderItem];
      data = existing;
    } else {
      data = colOrder;
    }

    return this.rbs.postRequest<any>(url, data, null, null, 'rb');
  }

  getColumns(): Observable<DynamicResponse<string, string[]>> {
    const url = `${this.baseUrl}/UserPreferences?prefName=columnVisibility`;

    return this.rbs.getRequest<any>(url, null, 'rb').pipe(
      tap(x => {
        this.columnsPreferences = x;
      })
    );
  }

  getSort(): Observable<DynamicResponse<string, any[]>> {
    const url = `${this.baseUrl}/UserPreferences?prefName=sortOrder`;

    return this.rbs.getRequest<any>(url, null, 'rb').pipe(
      tap(x => {
        this.sortPreferences = x;
      })
    );
  }

  getColOrder(): Observable<DynamicResponse<string, any[]>> {
    const url = `${this.baseUrl}/UserPreferences?prefName=colOrder`;

    return this.rbs.getRequest<any>(url, null, 'rb').pipe(
      tap(x => {
        this.orderPreferences = x;
      })
    );
  }

  saveTheme(theme: string) {
    const url = `${this.baseUrl}/UserPreferences?prefName=appTheme`;
    const data = { theme: theme };

    return this.rbs.postRequest(url, data, null, null, 'rb');
  }

  getTheme(): any {
    const url = `${this.baseUrl}/UserPreferences?prefName=appTheme`;

    return this.rbs.getRequest<any>(url, null, 'rb').subscribe({
      next: resp => {
        if (resp?.theme) {
          this.themeSvc.setTheme(resp.theme);
        }
      },
    });
  }

  saveColorScheme(scheme: string): Observable<void> {
    const url = `${this.baseUrl}/UserPreferences?prefName=colorScheme`;
    const data = { scheme: scheme };

    return this.rbs.postRequest(url, data, null, null, 'rb');
  }

  getColorScheme(): any {
    const url = `${this.baseUrl}/UserPreferences?prefName=colorScheme`;

    return this.rbs.getRequest<any>(url, null, 'rb').subscribe({
      next: resp => {
        if (resp?.scheme) {
          this.themeSvc.setColorScheme(resp.scheme);
        }
      },
    });
  }
}
